/**
 * Shared affiliation parsing helpers (audit S10 / H17).
 *
 * Three call sites previously implemented near-identical splitting and
 * filter-matching logic — small drifts between them produced bugs where
 * regular nodes and suggested nodes ended up in different combo parents
 * for the same affiliation string. Consolidating into one module fixes
 * the correctness issue and lets us cache the (raw → units) split.
 */

const _splitCache = new Map();

/**
 * Split a raw affiliation string into individual unit names.
 * Cached by raw string — repeated nodes with the same raw value reuse the
 * same result array.
 */
export const splitAffiliationUnits = (rawAffiliation) => {
  const key = String(rawAffiliation || '');
  const cached = _splitCache.get(key);
  if (cached) return cached;
  const units = key
    .split(/[\n\r;,|]+/)
    .map((value) => value.trim())
    .filter(Boolean);
  _splitCache.set(key, units);
  return units;
};

/**
 * Pick the affiliation unit that should drive cluster grouping for a node,
 * given the active filter selection.
 *
 * Returns `undefined` when clustering is disabled, the filter is empty, or
 * no unit matches.
 */
export const getClusterableAffiliation = (
  rawAffiliation,
  selectedKeys,
  clusterMode,
) => {
  if (!clusterMode || !selectedKeys || selectedKeys.length === 0) return undefined;
  if (!rawAffiliation) return undefined;

  const units = splitAffiliationUnits(rawAffiliation);
  for (const unit of units) {
    const normalized = unit.toLowerCase();
    if (selectedKeys.some((k) => normalized === k || normalized.includes(k))) {
      return unit;
    }
  }

  const fallback = String(rawAffiliation).trim();
  if (!fallback) return undefined;
  const normalizedFallback = fallback.toLowerCase();
  return selectedKeys.some((k) => normalizedFallback.includes(k)) ? fallback : undefined;
};

/**
 * Convenience wrapper accepting the affiliationFilter object form (a record
 * keyed by selected affiliation names) used by ReGraph chart consumers.
 */
export const getClusterableAffiliationFromFilter = (
  rawAffiliation,
  affiliationFilter,
  clusterMode,
) => {
  const keys = Object.keys(affiliationFilter || {}).map((k) => k.toLowerCase());
  return getClusterableAffiliation(rawAffiliation, keys, clusterMode);
};

export default {
  splitAffiliationUnits,
  getClusterableAffiliation,
  getClusterableAffiliationFromFilter,
};
